import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface TemplateResultDialogData {
  // The raw failureMessage/warningMessage text from the step, e.g.:
  // "1 of 3 check(s) failed:\nPASS $.email\nFAIL $.dob.age: expected >1000, was 77\nPASS $.gender"
  message: string;
}

interface ResultRow {
  passed: boolean;
  path: string;
  detail: string;
  // True when this check was VERIFY(...)-wrapped -- GlobalFunction.java
  // tags the path with a trailing " [verify]" marker (stripped back off in
  // parseRow below) specifically so a non-blocking failure can be shown
  // differently from one that actually stopped the scenario.
  isVerifyOnly: boolean;
}

const PASS_PREFIX = 'PASS ';
const FAIL_PREFIX = 'FAIL ';
const VERIFY_MARKER = ' [verify]';

// One entry within a field's list -- one array element's result for that
// field, e.g. "results[1]" -> the row for results[1].email.
interface IndexEntry {
  indexLabel: string;
  row: ResultRow;
}

// One field within an array-loop check (e.g. "email"), holding every
// element's result for that field in index order.
interface FieldGroup {
  fieldLabel: string;
  entries: IndexEntry[];
}

// One array-loop check (e.g. every row whose path starts with "$.results"),
// broken down field-first: field name, then that field's result across every
// index -- "$.results" -> "email" -> results[0]/[1]/[2]'s results, rather
// than index-first. Matches the "sub listing" the Configure dialog already
// uses for a loop check's own field preview, just organized the way you'd
// actually want to scan it: "did email fail anywhere?" over "what's in
// element 0?".
interface ArrayGroup {
  arrayPath: string;
  fieldGroups: FieldGroup[];
}

@Component({
  selector: 'app-template-result-dialog',
  templateUrl: './template-result-dialog.component.html',
  styleUrls: ['./template-result-dialog.component.scss'],
})
export class TemplateResultDialogComponent implements OnInit {
  rows: ResultRow[] = [];
  standaloneRows: ResultRow[] = [];
  arrayGroups: ArrayGroup[] = [];
  summary = '';
  passCount = 0;
  failCount = 0;
  allPassed = false;

  constructor(
    public dialogRef: MatDialogRef<TemplateResultDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TemplateResultDialogData
  ) {}

  ngOnInit(): void {
    this.parseMessage(this.data && this.data.message ? this.data.message : '');
  }

  private parseMessage(message: string): void {
    const lines = message.split('\n');
    lines.forEach((line) => {
      if (line.indexOf(PASS_PREFIX) === 0) {
        this.rows.push(this.parseRow(line.substring(PASS_PREFIX.length), true));
      } else if (line.indexOf(FAIL_PREFIX) === 0) {
        this.rows.push(this.parseRow(line.substring(FAIL_PREFIX.length), false));
      } else if (line.trim() !== '') {
        // The first line is the summary ("N of M check(s) failed:") — not a
        // PASS/FAIL row. Anything else unparseable is shown as-is so nothing
        // silently disappears if the message format ever changes upstream.
        this.summary = this.summary ? this.summary : line;
      }
    });
    this.passCount = this.rows.filter((r) => r.passed).length;
    this.failCount = this.rows.filter((r) => !r.passed).length;
    this.allPassed = this.rows.length > 0 && this.failCount === 0;
    // Only failures are worth scanning -- a passed check needs no attention,
    // so standaloneRows/arrayGroups are built from failures only. When
    // nothing failed, the template shows a single "all checks passed"
    // banner instead of this (now-empty) breakdown.
    this.buildArrayGroups(this.rows.filter((r) => !r.passed));
  }

  // Splits each row's path into the array field (everything before its
  // first index, e.g. "$.results"), the index itself (a number, or "?" for
  // a loop check that never got to run -- see GlobalFunction.java's
  // enumerateTemplateFields), and the field name after it (e.g. "email",
  // "dob.age"). A row with no bracket index at all isn't part of any
  // array-loop check and goes straight into standaloneRows unchanged.
  //
  // Grouped field-first (array -> field -> every index's result for it)
  // rather than index-first, so "did email fail anywhere across these
  // elements?" is one section to scan instead of scattered across N
  // per-element sections.
  // A field with more than one check (e.g. "VERIFY(>100) && VERIFY(>500)")
  // gets each check tagged onto its own result line's path by
  // GlobalFunction.java's evaluateTemplateDirective -- "dob.age [VERIFY(>100)]",
  // "dob.age [VERIFY(>500)]", possibly with a further trailing "[verify]" on
  // top of that. Left as-is, each distinct tag reads as a different field to
  // buildArrayGroups below, splitting one field's checks into separate
  // group headers. Strip trailing bracket tags to collapse them back under
  // one header -- each check's own detail line (e.g. "expected >100, was
  // 50") already says which condition it was, so nothing is lost. Only
  // non-numeric bracket content is stripped, so a genuine array index inside
  // the sub-template's own field name (e.g. "tags[2]") is left alone.
  private stripCheckTags(label: string): string {
    let result = label;
    const trailingBracket = /\s*\[([^\]]*)\]$/;
    let match = trailingBracket.exec(result);
    while (match && !/^\d+$/.test(match[1])) {
      result = result.slice(0, match.index);
      match = trailingBracket.exec(result);
    }
    return result;
  }

  private buildArrayGroups(rows: ResultRow[]): void {
    const pattern = /^(.*?)\[(\d+|\?)\]\.?(.*)$/;
    const groups: ArrayGroup[] = [];
    const groupIndexByArrayPath = new Map<string, number>();
    const standalone: ResultRow[] = [];

    rows.forEach((row) => {
      const match = pattern.exec(row.path);
      if (!match) {
        standalone.push(row);
        return;
      }
      const arrayPath = match[1];
      const index = match[2];
      const fieldLabel = this.stripCheckTags(match[3]) || '(this element)';

      if (!groupIndexByArrayPath.has(arrayPath)) {
        groupIndexByArrayPath.set(arrayPath, groups.length);
        groups.push({ arrayPath, fieldGroups: [] });
      }
      const arrGroup = groups[groupIndexByArrayPath.get(arrayPath)];
      let fieldGroup = arrGroup.fieldGroups.find((fg) => fg.fieldLabel === fieldLabel);
      if (!fieldGroup) {
        fieldGroup = { fieldLabel, entries: [] };
        arrGroup.fieldGroups.push(fieldGroup);
      }
      fieldGroup.entries.push({ indexLabel: arrayPath + '[' + index + ']', row });
    });

    this.standaloneRows = standalone;
    this.arrayGroups = groups;
  }

  arrayGroupFailCount(group: ArrayGroup): number {
    return group.fieldGroups.reduce((sum, fg) => sum + fg.entries.filter((e) => !e.row.passed).length, 0);
  }

  // Both PASS and FAIL lines share the same "path: detail" shape (e.g.
  // "$.email: matched \"a@b.com\"" or "$.dob.age: expected >1000, was 39") --
  // one parser for both, just tagged with which side it came from.
  private parseRow(rest: string, passed: boolean): ResultRow {
    const separatorIndex = rest.indexOf(': ');
    const rawPath = separatorIndex === -1 ? rest : rest.substring(0, separatorIndex);
    const detail = separatorIndex === -1 ? '' : rest.substring(separatorIndex + 2);
    // Usually a trailing suffix, but a non-blocking "Array object" loop
    // check tags its base path with this marker too (GlobalFunction.java's
    // matchTemplateNode), which then has "[index].field" appended after it
    // for every check inside the loop -- landing the marker in the middle
    // of the final path rather than at the end. A field that's also
    // individually Verify-wrapped inside that loop can end up with the
    // marker twice. Detect/strip every occurrence rather than assuming
    // it's only ever the last thing in the string.
    const isVerifyOnly = rawPath.indexOf(VERIFY_MARKER) !== -1;
    const path = isVerifyOnly ? rawPath.split(VERIFY_MARKER).join('') : rawPath;
    return { passed, path, detail, isVerifyOnly };
  }

  close(): void {
    this.dialogRef.close();
  }
}
